import React, { useState, useEffect } from "react";
import { Popover, Box, Typography, MenuItem, TextField, FormControlLabel, Checkbox, FormGroup, Radio, RadioGroup } from "@mui/material";
import { TrackNowButton } from "../ui";
import { FilterMatchMode } from "primereact/api";
import { tokens } from "../../theme/tokens";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function FilingMasterTableFilter({
  anchorEl,
  onClose,
  onApply,
  onClear,
  masters,
  categoryList,
  stateList
}) {
  const [selectedColumn, setSelectedColumn] = useState("");
  const [filterType, setFilterType] = useState("");
  const [textValue, setTextValue] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedValues, setSelectedValues] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);
  const filingFrequencyOptions = [
    "Annually",
    "Semi-Annually",
    "Semi-Annual",
    "Quarterly",
    "Monthly",
    "Weekly",
    "Daily",
  ];

  // Define filterable columns for filing master
  const getFilterableColumns = () => {
    return [
      { value: "filingName", label: "Filing Name", type: "text" },
      { value: "filingFrequency", label: "Filing Frequency", type: "radio" },
      { value: "stateList", label: "State", type: "multiselect" },
      { value: "ruleInfo", label: "Rule Info", type: "text" },
      { value: "required", label: "Required", type: "multiselect" },
      { value: "federalCategories", label: "Who Must File Federal", type: "multiselect" },
      { value: "stateCategories", label: "Who Must File in State", type: "multiselect" },
      { value: "jsicontactName", label: "JSI Staff", type: "text" },
      { value: "juristiction", label: "Jurisdiction", type: "radio" },
      { value: "dueDate", label: "Due Date", type: "date" },
      { value: "whereToFile", label: "Where to File", type: "text" },
    ];
  };

  const getFilterOptions = () => {
    const column = getFilterableColumns().find(col => col.value === selectedColumn);
    if (!column) return [];

    if (column.type === "date") {
      return [
        { value: "dateIs", label: "Date Is" },
        { value: "dateIsNot", label: "Date Is Not" },
        { value: "dateBefore", label: "Date Is Before" },
        { value: "dateAfter", label: "Date Is After" }
      ];
    } else if (selectedColumn === "filingFrequency") {
      return [
        { value: FilterMatchMode.EQUALS, label: "Is" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" }
      ];
    } else if (column.type === "text") {
      return [
        { value: FilterMatchMode.CONTAINS, label: "Contains" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" },
        { value: FilterMatchMode.EQUALS, label: "Is" }
      ];
    } else if (selectedColumn === "required" || selectedColumn === "juristiction") {
      return [
        { value: FilterMatchMode.EQUALS, label: "Is" }
      ];
    } else {
      return [
        { value: FilterMatchMode.IN, label: "Contains" },
        { value: FilterMatchMode.NOT_CONTAINS, label: "Is Not" },
        { value: FilterMatchMode.EQUALS, label: "Is" }
      ];
    }
  };

  const getUniqueValues = (data, field) => {
    const values = data
      .map(item => item[field])
      .filter((value) => value !== null && value !== undefined && value !== "");
    return [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b)));
  };

  const getOptionLabel = (option) => {
    if (option === true) return "True";
    if (option === false) return "False";
    if (option === null || option === undefined) return "";
    return String(option);
  };

  const getAvailableValues = () => {
    if (!selectedColumn || !masters) return [];

    switch (selectedColumn) {
      case "filingFrequency":
        return Array.from(
          new Set([
            ...filingFrequencyOptions,
            ...getUniqueValues(masters, "filingFrequency"),
          ])
        );
      case "stateList":
        return stateList || [];
      case "required":
        return [true, false];
      case "federalCategories":
        return categoryList || [];
      case "stateCategories":
        return categoryList || [];
      case "juristiction":
        return ["Federal", "State"];
      default:
        return [];
    }
  };

  useEffect(() => {
    setAvailableOptions(getAvailableValues());
    setSelectedValues([]);
    setTextValue("");
    setSelectedDate(null);
    setFilterType("");
  }, [selectedColumn]);

  const handleColumnChange = (value) => {
    setSelectedColumn(value);
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
  };

  const handleValueChange = (value) => {
    const selectedColumnType = getFilterableColumns().find(
      (col) => col.value === selectedColumn
    )?.type;

    if (
      selectedColumn === "required" ||
      selectedColumn === "juristiction" ||
      selectedColumnType === "radio"
    ) {
      setSelectedValues([value]);
      return;
    }

    if (getFilterableColumns().find(col => col.value === selectedColumn)?.type === "multiselect") {
      const newValues = selectedValues.includes(value)
        ? selectedValues.filter(v => v !== value)
        : [...selectedValues, value];
      setSelectedValues(newValues);
    } else {
      setTextValue(value);
    }
  };

  const formatDateForApi = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  };

  const handleApply = () => {
    if (!selectedColumn || !filterType) return;

    const column = getFilterableColumns().find(col => col.value === selectedColumn);
    const filterValue =
      column.type === "multiselect"
        ? selectedValues
        : column.type === "radio"
          ? selectedValues[0] ?? ""
          : column.type === "date"
            ? formatDateForApi(selectedDate)
          : textValue;

    if ((column.type === "multiselect" && selectedValues.length === 0) || 
        (column.type === "radio" && selectedValues.length === 0) ||
        (column.type === "date" && !selectedDate) ||
        (column.type === "text" && !textValue.trim())) {
      return;
    }

    const filterConfig = {
      field: selectedColumn,
      matchMode: filterType,
      value: filterValue
    };

    onApply(filterConfig);
    
    // Reset form fields
    setSelectedColumn("");
    setFilterType("");
    setTextValue("");
    setSelectedDate(null);
    setSelectedValues([]);
    setAvailableOptions([]);
    
    onClose();
  };

  const handleClear = () => {
    setSelectedColumn("");
    setFilterType("");
    setTextValue("");
    setSelectedDate(null);
    setSelectedValues([]);
    setAvailableOptions([]);
    onClear();
    onClose();
  };

  const renderValueInput = () => {
    const column = getFilterableColumns().find(col => col.value === selectedColumn);
    if (!column) return null;

    if (column.type === "date") {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Select Date:</Typography>
          <Box
            sx={{
              border: "1px solid #ccc",
              borderRadius: 1,
              p: 1,
              backgroundColor: tokens.colors.offWhite
            }}
          >
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              inline
              calendarClassName="filing-master-filter-datepicker"
            />
          </Box>
        </Box>
      );
    } else if (column.type === "text") {
      return (
        <TextField
          fullWidth
          label="Value"
          size="small"
          sx={{ 
            mt: 2,
            '& .MuiOutlinedInput-root': {
              backgroundColor: tokens.colors.offWhite,
              '& fieldset': {
                borderColor: '#ccc',
              },
              '&:hover fieldset': {
                borderColor: tokens.colors.connectedPurple,
              },
            },
          }}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
        />
      );
    } else if (column.type === "radio") {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Select Value:</Typography>
          <Box sx={{
            border: '1px solid #ccc',
            borderRadius: 1,
            p: 1.5,
            backgroundColor: tokens.colors.offWhite
          }}>
            <RadioGroup
              value={selectedValues[0] ?? ""}
              onChange={(e) => handleValueChange(e.target.value)}
            >
              {availableOptions.map((option) => (
                <FormControlLabel
                  key={`${selectedColumn}-${String(option)}`}
                  value={option}
                  control={<Radio size="small" />}
                  label={getOptionLabel(option)}
                  sx={{
                    fontSize: '14px',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px'
                    },
                    mb: 0.5
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
        </Box>
      );
    } else {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Select Values:</Typography>
          <Box sx={{ 
            maxHeight: 200, 
            overflowY: 'auto', 
            border: '1px solid #ccc', 
            borderRadius: 1, 
            p: 1,
            backgroundColor: tokens.colors.offWhite
          }}>
            <FormGroup>
              {availableOptions.map((option) => (
                <FormControlLabel
                  key={`${selectedColumn}-${String(option)}`}
                  control={
                    <Checkbox
                      checked={selectedValues.includes(option)}
                      onChange={() => handleValueChange(option)}
                      size="small"
                    />
                  }
                  label={getOptionLabel(option)}
                  sx={{ 
                    fontSize: '14px',
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px'
                    },
                    mb: 0.5
                  }}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      PaperProps={{
        sx: {
          backgroundColor: tokens.colors.offWhite,
          border: '1px solid #ccc'
        }
      }}
    >
      <Box sx={{ p: 2, width: 350, maxHeight: 500, overflowY: 'auto' }}>
        <style>{`
          .filing-master-filter-datepicker.react-datepicker {
            background: ${tokens.colors.offWhite};
            border: 1px solid #ccc;
            border-radius: 8px;
            font-family: inherit;
          }
          .filing-master-filter-datepicker .react-datepicker__header {
            background: ${tokens.colors.offWhite};
            border-bottom: 1px solid #ccc;
          }
          .filing-master-filter-datepicker .react-datepicker__day,
          .filing-master-filter-datepicker .react-datepicker__day-name {
            color: #333;
          }
          .filing-master-filter-datepicker .react-datepicker__day--selected,
          .filing-master-filter-datepicker .react-datepicker__day--keyboard-selected {
            background: ${tokens.colors.connectedPurple} !important;
            color: #fff !important;
            border-radius: 999px !important;
          }
        `}</style>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>Filter</Typography>

        <TextField 
          select 
          fullWidth 
          label="Column" 
          size="small"
          value={selectedColumn}
          onChange={(e) => handleColumnChange(e.target.value)}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: tokens.colors.offWhite,
              '& fieldset': {
                borderColor: '#ccc',
              },
              '&:hover fieldset': {
                borderColor: tokens.colors.connectedPurple,
              },
            },
          }}
        >
          {getFilterableColumns().map((column) => (
            <MenuItem key={column.value} value={column.value}>
              {column.label}
            </MenuItem>
          ))}
        </TextField>

        {selectedColumn && (
          <TextField 
            select 
            fullWidth 
            label="Condition" 
            size="small" 
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: tokens.colors.offWhite,
                '& fieldset': {
                  borderColor: '#ccc',
                },
                '&:hover fieldset': {
                  borderColor: tokens.colors.connectedPurple,
                },
              },
            }}
            value={filterType}
            onChange={(e) => handleFilterTypeChange(e.target.value)}
          >
            {getFilterOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}

        {selectedColumn && filterType && renderValueInput()}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          <TrackNowButton size="small" variant="secondary" onClick={handleClear}>
            Clear
          </TrackNowButton>
          <TrackNowButton size="small" variant="primary" onClick={handleApply}>
            Apply Filter
          </TrackNowButton>
        </Box>
      </Box>
    </Popover>
  );
}
